import { openDB } from 'idb';
import { DATA_VERSION } from './version';

export const IDB_OPEN_TIMEOUT_MS = 3000;

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
    this.dbPromise = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;
    if (!this.dbPromise) {
      const dbPromise = openDB(dbName, DATA_VERSION, {
        upgrade(db) {
          db.createObjectStore('cache-keys');
          db.createObjectStore('inbox', { keyPath: 'id' });
          db.createObjectStore('label', { keyPath: 'id' });
          db.createObjectStore('team', { keyPath: 'id' });
        },
        // Another tab wants to delete (logout) or upgrade this database. If we
        // keep the connection open, its request stays blocked and every later
        // open of this database, in any tab, queues behind it forever.
        blocking: (_currentVersion, _blockedVersion, event) => {
          event.target.close();
          this.resetDb(dbPromise);
        },
        terminated: () => this.resetDb(dbPromise),
      }).catch(error => {
        this.resetDb(dbPromise);
        throw error;
      });
      this.dbPromise = dbPromise;
    }

    // The open request can stay pending indefinitely (e.g. behind a blocked
    // deleteDatabase). Give up so callers can fall back to the network.
    let timeoutId;
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error(`Timed out opening IndexedDB ${dbName}`)),
        IDB_OPEN_TIMEOUT_MS
      );
    });
    const dbPromise = this.dbPromise;
    let db;
    try {
      db = await Promise.race([dbPromise, timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
    // The connection was closed or replaced while we were waiting.
    if (this.dbPromise !== dbPromise) {
      throw new Error(`IndexedDB ${dbName} was closed while opening`);
    }
    this.db = db;

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  // Only reset when the closing connection is still the current one, so a
  // late event from an older connection can't drop a newer one.
  resetDb(dbPromise) {
    if (this.dbPromise !== dbPromise) return;
    this.db = null;
    this.dbPromise = null;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    await Promise.all(
      Object.keys(cacheKeys).map(modelName =>
        this.db.put('cache-keys', cacheKeys[modelName], modelName)
      )
    );
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
